<template>
  <div>
    <div class="kt-login__head">
      <div
        class="kt-header__topbar d-flex align-items-center justify-content-end cus-topbar"
      >
        <span>{{ $t("TRANSLATOR.SELECT") }}</span>
        <!--begin: Language bar -->
        <div class="kt-header__topbar-item kt-header__topbar-item--langs">
          <div
            class="kt-header__topbar-wrapper"
            id="kt_language_toggle"
            data-toggle="dropdown"
          >
            <span class="kt-header__topbar-icon">
              <img :src="languageFlag || defaultLanguageFlag" alt="" />
            </span>
          </div>

          <div
            class="dropdown-menu dropdown-menu-fit dropdown-menu-right"
            v-on:click.stop
          >
            <KTDropdownLanguage
              v-on:language-changed="onLanguageChanged"
            ></KTDropdownLanguage>
          </div>
        </div>
        <!--end: Language bar -->
      </div>
    </div>

    <!--begin::Body-->
    <div class="kt-login__body">
      <!--begin::Signin-->
      <div class="kt-login__form">
        <div class="kt-login__title">
          <h3>{{ $t("AUTH.LOGIN.TITLE") }}</h3>
        </div>

        <!--begin::Form-->
        <b-form class="kt-form" @submit.stop.prevent="onSubmit">
          <div
            v-if="errors && errors.length && !form.token"
            role="alert"
            class="alert alert-danger"
          >
            <div class="alert-text" v-for="(error, i) in errors" :key="i">
              {{ $t(error) }}
            </div>
          </div>

          <b-form-group
            id="login-email-group"
            :label="$t('COMMON.USERNAME')"
            label-for="login-email"
          >
            <b-form-input
              id="login-email"
              name="login-email"
              v-model="$v.form.email.$model"
              :state="validateState('email')"
              aria-describedby="input-1-live-feedback"
            ></b-form-input>

            <b-form-invalid-feedback id="input-1-live-feedback">
              {{ $t("AUTH.VALIDATION.EMAIL") }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            id="login-password-group"
            :label="$t('AUTH.INPUT.PASSWORD')"
            label-for="login-password"
            class="mt-4"
          >
            <b-form-input
              type="password"
              id="login-password"
              name="login-password"
              v-model="$v.form.password.$model"
              :state="validateState('password')"
              aria-describedby="input-2-live-feedback"
            ></b-form-input>

            <b-form-invalid-feedback id="input-2-live-feedback">
              {{ $t("AUTH.VALIDATION.PASSWORD") }}
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- Forgot password link belongs near password -->
          <div class="d-flex justify-content-end mt-2">
            <a
              href="#"
              class="kt-link kt-login__link-forgot"
              v-b-modal.forgot-modal
            >
              {{ $t("AUTH.FORGOT.TITLE") }}
            </a>
          </div>

          <!--begin::Action-->
          <div
            class="kt-login__actions d-flex align-items-center justify-content-between mt-4"
          >
            <b-button
              type="button"
              variant="link"
              class="p-0 kt-link"
              v-on:click="clearForm"
            >
              {{ $t("AUTH.CLEAR") }}
            </b-button>

            <b-button
              type="submit"
              id="kt_submit"
              :disabled="loginSubmitting"
              class="btn btn-primary btn-elevate kt-login__btn-primary"
            >
              {{ $t("AUTH.LOGIN.BUTTON") }}
            </b-button>
          </div>
          <!--end::Action-->
        </b-form>
        <!--end::Form-->
      </div>
      <!--end::Signin-->

      <!-- ----------- twofa modal -->
      <b-modal
        id="twofa-modal"
        ref="twofa"
        :title="$t('TWOFA.TFAUTH')"
        @shown="onLoginOtpModalShown"
        @hidden="onLoginOtpModalHidden"
      >
        <b-container fluid>
          <b-alert show variant="info">
            {{ $t("AUTH.LOGIN.DESC3") }}
          </b-alert>

          <b-alert v-if="loginOtpError" show variant="danger">
            {{ $t(loginOtpError) }}
          </b-alert>

          <b-row class="my-3">
            <b-col sm="12">
              <div class="otp-center">
                <label class="d-block mb-2" for="login-otp-0">
                  {{ $t("AUTH.LOGIN.TFA") }}
                </label>

                <div class="otp-wrap" @paste.prevent="onLoginOtpPaste">
                  <input
                    v-for="(d, idx) in loginOtpDigits"
                    :key="'login-otp-' + idx"
                    :id="'login-otp-' + idx"
                    :ref="'login-otp-' + idx"
                    class="otp-input"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    autocomplete="one-time-code"
                    maxlength="1"
                    :value="loginOtpDigits[idx]"
                    :disabled="loginOtpSubmitting"
                    @input="onLoginOtpInput($event, idx)"
                    @keydown="onLoginOtpKeydown($event, idx)"
                    @focus="onLoginOtpFocus(idx)"
                    :aria-label="$t('TWOFA.OTP_DIGIT', { digit: idx + 1 })"
                  />
                </div>
              </div>
            </b-col>
          </b-row>
        </b-container>

        <template v-slot:modal-footer>
          <b-button
            variant="outline-secondary"
            v-on:click="modalClose('twofa')"
          >
            {{ $t("COMMON.CANCEL") }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="loginOtpSubmitting"
            v-on:click="checkTwofa()"
          >
            {{ $t("AUTH.LOGIN.BUTTON") }}
          </b-button>
        </template>
      </b-modal>
      <!--end::twofa-->

      <!-- Password recovery has its own challenge flow, separate from login OTP. -->
      <b-modal
        id="forgot-modal"
        ref="forgotPassword"
        :title="$t('AUTH.FORGOT.TITLE')"
        hide-footer
        @show="forgotPasswordOpen = true"
        @hide="forgotPasswordOpen = false"
      >
        <PasswordRecovery
          v-if="forgotPasswordOpen"
          stage="send_link"
          @completed="onRecoveryLinkSent"
          @cancel="modalClose('forgotPassword')"
        />
      </b-modal>
      <!-- ---------- change password modal end------------- -->
    </div>
    <!--end::Body-->
  </div>
</template>

<style lang="scss" scoped>
.kt-spinner.kt-spinner--right:before {
  right: 8px;
}

/* OTP UI (same feel as Twofa page) */

.otp-center {
  display: flex;
  flex-direction: column;
  align-items: center; /* centers label + row */
  width: 100%;
}

.otp-wrap--center {
  justify-content: center; /* centers the 6 boxes row */
}

.otp-wrap {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.otp-input {
  width: 52px;
  height: 56px;
  font-size: 26px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  outline: none;
}

.otp-input:focus {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.2);
}
</style>

<script>
import { mapState } from "vuex";
import { LOGIN_STEP1, LOGIN_STEP2, SET_ERROR } from "@/store/auth.module";
import { hydrateAdminSession } from "@/common/admin-session";

import { validationMixin } from "vuelidate";
import { minLength, required } from "vuelidate/lib/validators";
import KTDropdownLanguage from "@/views/theme/topbar/DropdownLanguage.vue";
import i18nService from "@/common/i18n.service.js";
import PasswordRecovery from "@/views/partials/PasswordRecovery.vue";

export default {
  mixins: [validationMixin],
  name: "login",
  data() {
    return {
      form: {
        email: "",
        password: "",
        token: "",
      },

      forgotPasswordOpen: false,
      loginSubmitting: false,

      // login 2FA modal OTP
      loginOtpDigits: ["", "", "", "", "", ""],
      loginOtpError: "",
      loginOtpSubmitting: false,

      languageFlag: "",
      languages: i18nService.languages,
    };
  },
  components: {
    KTDropdownLanguage,
    PasswordRecovery,
  },
  validations: {
    form: {
      email: {
        required,
        // email
      },
      password: {
        required,
        minLength: minLength(3),
      },
    },
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    onLanguageChanged() {
      const current = this.languages.find((val) => {
        return val.lang === i18nService.getActiveLanguage();
      });
      this.languageFlag = current ? current.flag : "";
    },

    modalClose(item) {
      this.form.token = null;

      // reset OTP when closing 2FA modal
      if (item === "twofa") {
        this.resetLoginOtp();
      }

      this.$nextTick(() => {
        this.$v.$reset();
      });
      this.$refs[item].hide();
    },

    clearForm() {
      this.form.email = null;
      this.form.password = null;
      this.$store.commit(SET_ERROR, []);

      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    onSubmit() {
      if (this.loginSubmitting) return;
      this.$v.form.$touch();
      if (this.$v.form.email.$anyError || this.$v.form.password.$anyError) {
        return;
      }

      const username = this.$v.form.email.$model;
      const password = this.$v.form.password.$model;
      this.form.token = null;
      this.loginSubmitting = true;

      // set spinner to submit button
      const submitButton = document.getElementById("kt_submit");
      submitButton.classList.add(
        "kt-spinner",
        "kt-spinner--light",
        "kt-spinner--right"
      );

      return this.$store
        .dispatch(LOGIN_STEP1, {
          username,
          password,
          action: "adminlogin_step1",
        })
        .then((res) => {
          if (res.returncode !== 1) return;
          if (
            res.returncode == 1 &&
            !res.returndata.otprequired &&
            res.returndata.token
          ) {
            if (res.returndata.mfa_enrollment_required === true) {
              this.$router.replace("/mfa-enrollment");
            } else {
              return this.completeLogin(res.returndata.token);
            }
          } else if (res.returndata.otprequired) {
            // show 2fa window
            this.form.token = res.returndata.token;
            this.$refs["twofa"].show();
          }
        })
        .catch(() => undefined) // The store supplies the visible login error.
        .finally(() => {
          this.loginSubmitting = false;
          submitButton.classList.remove(
            "kt-spinner",
            "kt-spinner--light",
            "kt-spinner--right"
          );
        });
    },

    completeLogin(token) {
      return hydrateAdminSession(token)
        .then(() => this.$router.push("home", () => this.$router.go(0)))
        .catch(() => this.makeToastVariant("danger", this.$t("COMMON.ERROR")));
    },

    onRecoveryLinkSent(message) {
      this.makeToastVariant("success", message);
      this.modalClose("forgotPassword");
    },

    // ---------- Login 2FA OTP helpers ----------
    resetLoginOtp() {
      this.loginOtpDigits = ["", "", "", "", "", ""];
      this.loginOtpError = "";
      this.loginOtpSubmitting = false;
    },

    focusLoginOtp(idx) {
      this.$nextTick(() => {
        const el = this.$refs["login-otp-" + idx];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) input.focus();
        if (input && input.select) input.select();
      });
    },

    onLoginOtpModalShown() {
      this.resetLoginOtp();
      this.focusLoginOtp(0);
    },

    onLoginOtpModalHidden() {
      this.form.token = null;
      this.$store.commit(SET_ERROR, []);
      this.resetLoginOtp();
    },

    onLoginOtpFocus(idx) {
      const firstEmpty = this.loginOtpDigits.findIndex((d) => d === "");
      if (firstEmpty !== -1 && idx > firstEmpty) {
        this.focusLoginOtp(firstEmpty);
      }
    },

    onLoginOtpInput(e, idx) {
      const raw = e && e.target && e.target.value ? String(e.target.value) : "";
      const digit = raw.replace(/\D/g, "").slice(-1);

      this.$set(this.loginOtpDigits, idx, digit);
      this.loginOtpError = "";

      if (digit && idx < 5) {
        this.focusLoginOtp(idx + 1);
      }

      if (this.loginOtpDigits.every((d) => /^[0-9]$/.test(d))) {
        this.checkTwofa(); // auto-submit
      }
    },

    onLoginOtpKeydown(e, idx) {
      const key = e.key;

      if (key === "Backspace") {
        e.preventDefault();
        if (this.loginOtpDigits[idx]) {
          this.$set(this.loginOtpDigits, idx, "");
        } else if (idx > 0) {
          this.$set(this.loginOtpDigits, idx - 1, "");
          this.focusLoginOtp(idx - 1);
        }
        return;
      }

      if (key === "ArrowLeft" && idx > 0) {
        e.preventDefault();
        this.focusLoginOtp(idx - 1);
        return;
      }
      if (key === "ArrowRight" && idx < 5) {
        e.preventDefault();
        this.focusLoginOtp(idx + 1);
        return;
      }

      if (key.length === 1 && !/[0-9]/.test(key) && key !== "Tab") {
        e.preventDefault();
      }
    },

    onLoginOtpPaste(e) {
      const txt = (e.clipboardData && e.clipboardData.getData("text")) || "";
      const digits = String(txt).replace(/\D/g, "").slice(0, 6);
      if (!digits) return;
      this.loginOtpError = "";

      const arr = digits.split("");
      for (let i = 0; i < 6; i++) {
        this.$set(this.loginOtpDigits, i, arr[i] || "");
      }

      if (digits.length === 6) {
        this.checkTwofa(); // auto-submit
      } else {
        this.focusLoginOtp(Math.min(digits.length, 5));
      }
    },

    clearLoginOtp() {
      this.loginOtpDigits = ["", "", "", "", "", ""];
    },

    checkTwofa() {
      if (this.loginOtpSubmitting) return;

      const complete = this.loginOtpDigits.every((d) => /^[0-9]$/.test(d));
      if (!complete) {
        this.loginOtpError = "VALIDATION.REQUIRED_FIELD";
        return;
      }

      this.loginOtpSubmitting = true;
      this.loginOtpError = "";

      const username = this.$v.form.email.$model;
      const otpcode = this.loginOtpDigits.join("");
      const token = this.form.token;

      return this.$store
        .dispatch(LOGIN_STEP2, {
          username,
          otpcode,
          token,
          action: "adminlogin_step2",
        })
        .then((res) => {
          if (res.returncode == 1 && res.returndata) {
            return this.completeLogin(res.returndata);
          } else {
            this.loginOtpError = "AUTH.LOGIN.TFA_INVALID";
            this.clearLoginOtp();
          }
        })
        .catch(() => {
          this.loginOtpError = "COMMON.ERROR";
          this.clearLoginOtp();
        })
        .finally(() => {
          this.loginOtpSubmitting = false;
          // The modal portal updates its inputs after the login view updates.
          if (this.loginOtpError) {
            this.$nextTick(() => this.focusLoginOtp(0));
          }
        });
    },

    /* ----notify---- */
    makeToastVariant(variant = null, msg) {
      let title = this.$t("COMMON.ERROR");
      if (variant == "success") {
        title = this.$t("COMMON.SUCCESS");
      }
      this.$bvToast.toast(msg, {
        title: title,
        variant: variant,
        toaster: "b-toaster-top-center",
        solid: true,
      });
    },
  },

  computed: {
    ...mapState({
      errors: (state) => state.auth.errors,
    }),

    backgroundImage() {
      return process.env.BASE_URL + "assets/media/bg/bg-4.jpg";
    },

    defaultLanguageFlag() {
      const current = this.languages.find(
        (val) => val.lang === i18nService.getActiveLanguage()
      );
      return current ? current.flag : "";
    },
  },

  mounted() {
    // Ensure flag is correct on first render
    this.onLanguageChanged();
  },
};
</script>
